import { useEffect, useState } from 'react';
import { User, Session, Factor } from '@supabase/supabase-js';
import { supabase, isDemoMode } from '../lib/supabase';
import { REQUIRE_MFA } from '../config/env';
import { logSecurityEvent } from '../utils/securityUtils';
import { signInWithTrustedPasswordTelemetry } from '../services/trustedPasswordLogin';

// MFA Status types
export type MfaStatus = 'none' | 'required' | 'enroll_required' | 'verifying' | 'verified' | 'unavailable';

export interface MfaState {
  status: MfaStatus;
  factors: Factor[];
  selectedFactorId: string | null;
  challengeId: string | null;
}

export interface LoginResult {
  ok: boolean;
  mfaRequired?: boolean;
  mfaEnrollRequired?: boolean;
  error?: string;
}

export function useSupabaseAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // MFA state
  const [mfaState, setMfaState] = useState<MfaState>({
    status: 'none',
    factors: [],
    selectedFactorId: null,
    challengeId: null
  });

  useEffect(() => {
    // In demo mode, skip Supabase auth entirely
    if (isDemoMode || !supabase) {
      setLoading(false);
      return;
    }

    let active = true;
    let sessionRevision = 0;

    const applySession = async (nextSession: Session | null, method: string) => {
      const revision = ++sessionRevision;
      setLoading(true);
      setSession(nextSession);
      setUser(nextSession?.user ?? null);

      if (!nextSession?.user) {
        if (active && revision === sessionRevision) {
          setMfaState({
            status: 'none',
            factors: [],
            selectedFactorId: null,
            challengeId: null,
          });
          setLoading(false);
        }
        return;
      }

      let currentAAL: 'aal1' | 'aal2' | null = null;
      let mfaResolutionError: string | null = null;
      let nextState: MfaState;
      try {
        const [
          { data: aalData, error: aalError },
          { data: factorsData, error: factorsError },
        ] = await Promise.all([
          supabase!.auth.mfa.getAuthenticatorAssuranceLevel(),
          supabase!.auth.mfa.listFactors(),
        ]);
        if (aalError) throw aalError;
        if (factorsError) throw factorsError;

        if (aalData?.currentLevel === 'aal2') {
          currentAAL = 'aal2';
        } else if (aalData?.currentLevel === 'aal1') {
          currentAAL = 'aal1';
        }
        const verifiedFactors = (factorsData?.totp || []).filter(f => f.status === 'verified');
        if (currentAAL === 'aal2') {
          nextState = {
            status: 'verified',
            factors: verifiedFactors,
            selectedFactorId: verifiedFactors[0]?.id ?? null,
            challengeId: null,
          };
        } else if (verifiedFactors.length > 0) {
          nextState = {
            status: 'required',
            factors: verifiedFactors,
            selectedFactorId: verifiedFactors[0].id,
            challengeId: null,
          };
        } else if (REQUIRE_MFA) {
          // A restored AAL1 session must re-enter first-time enrollment. Never
          // treat absence of a factor as an MFA-policy bypass.
          nextState = {
            status: 'enroll_required',
            factors: [],
            selectedFactorId: null,
            challengeId: null,
          };
        } else {
          nextState = {
            status: 'none',
            factors: [],
            selectedFactorId: null,
            challengeId: null,
          };
        }
      } catch (error) {
        console.error('Failed to resolve MFA state:', error);
        mfaResolutionError = error instanceof Error ? error.message : 'MFA state unavailable';
        // A policy-enabled deployment stays blocked when factor/AAL resolution
        // fails. Do not guess that enrollment is needed: a temporary factor API
        // failure must not create a duplicate factor or expose the product.
        nextState = {
          status: REQUIRE_MFA ? 'unavailable' : 'none',
          factors: [],
          selectedFactorId: null,
          challengeId: null,
        };
      }

      if (!active || revision !== sessionRevision) return;
      setMfaState(nextState);
      setLoading(false);
      logSecurityEvent('login', nextSession.user.id, {
        method,
        email: nextSession.user.email,
        aal: currentAAL,
        mfaStatus: nextState.status,
        ...(mfaResolutionError ? { error: mfaResolutionError } : {}),
      }, mfaResolutionError ? 'failure' : 'success');
    };

    void supabase.auth.getSession().then(({ data: { session: initialSession } }) => (
      applySession(initialSession, 'session_restore')
    ));

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      void applySession(nextSession, 'auth_state_change');
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string, metadata?: Record<string, unknown>) => {
    // Guard: return error if Supabase is not available
    if (!supabase) {
      return { data: null, error: new Error('Supabase not available in demo mode') };
    }

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata,
          emailRedirectTo: `${window.location.origin}/auth/callback`
        },
      });

      // Log signup attempt
      if (error) {
        logSecurityEvent('user_creation', 'system', {
          method: 'signup',
          email,
          error: error.message
        }, 'failure');
      } else if (data.user) {
        logSecurityEvent('user_creation', data.user.id, {
          method: 'signup',
          email
        }, 'success');
      }

      return { data, error };
    } catch (err) {
      console.error('Signup error:', err);
      logSecurityEvent('user_creation', 'system', {
        method: 'signup',
        email,
        error: err instanceof Error ? err.message : 'Unknown error'
      }, 'failure');

      return { data: null, error: err instanceof Error ? err : new Error('Unknown error') };
    }
  };

  const signIn = async (email: string, password: string): Promise<LoginResult> => {
    // Guard: return error if Supabase is not available
    if (!supabase) {
      console.error('[signIn] supabase client is null — env vars not loaded or IS_DEMO_MODE=true');
      return { ok: false, error: 'Supabase not available in demo mode' };
    }

    try {
      const { data, error } = await signInWithTrustedPasswordTelemetry(
        supabase,
        email,
        password,
      );

      if (error) {
        return { ok: false, error: error.message };
      }

      if (data.user) {
        logSecurityEvent('login', data.user.id, {
          method: 'trusted_password_proxy'
        }, 'success');

        // Check MFA requirements
        const mfaResult = await checkMfaRequirements(data.user.id);

        if (mfaResult.mfaRequired) {
          return { ok: true, mfaRequired: true };
        }

        if (mfaResult.mfaEnrollRequired) {
          return { ok: true, mfaEnrollRequired: true };
        }
      }

      return { ok: true };
    } catch (err) {
      console.error('Login error:', err);
      return { ok: false, error: err instanceof Error ? err.message : 'Unknown error' };
    }
  };

  /**
   * Check if MFA is required for user after password auth
   */
  const checkMfaRequirements = async (userId: string): Promise<{ mfaRequired: boolean; mfaEnrollRequired: boolean }> => {
    if (!supabase) return { mfaRequired: false, mfaEnrollRequired: false };

    try {
      // Get MFA factors from Supabase Auth
      const { data: factorsData, error: factorsError } = await supabase.auth.mfa.listFactors();

      if (factorsError) throw factorsError;

      const { data: aalData, error: aalError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (aalError) throw aalError;

      if (aalData?.currentLevel === 'aal2') {
        const verifiedFactors = (factorsData?.totp || []).filter(f => f.status === 'verified');
        setMfaState({
          status: 'verified',
          factors: verifiedFactors,
          selectedFactorId: verifiedFactors[0]?.id ?? null,
          challengeId: null,
        });
        return { mfaRequired: false, mfaEnrollRequired: false };
      }

      const totpFactors = factorsData?.totp || [];
      const verifiedFactors = totpFactors.filter(f => f.status === 'verified');
      const hasFactor = verifiedFactors.length > 0;

      // Check user's 2FA setting from profile
      const { data: profileData } = await supabase
        .from('profiles')
        .select('two_factor_enabled')
        .eq('id', userId)
        .single();

      const twoFactorEnabled = profileData?.two_factor_enabled ?? false;

      // Deployment-level MFA policy until workspace-scoped security settings exist.
      const twoFactorRequired = REQUIRE_MFA;

      // Determine MFA requirement
      if (hasFactor) {
        // User has verified factor - require MFA
        const selectedFactor = verifiedFactors[0];
        setMfaState({
          status: 'required',
          factors: verifiedFactors,
          selectedFactorId: selectedFactor.id,
          challengeId: null
        });
        return { mfaRequired: true, mfaEnrollRequired: false };
      }

      // If org requires 2FA OR profile says 2FA is enabled but no factor exists,
      // user must enroll (prevents stuck state if factor was deleted)
      if ((twoFactorRequired || twoFactorEnabled) && !hasFactor) {
        // Clear the stale profile flag since factor doesn't exist
        if (twoFactorEnabled && !twoFactorRequired) {
          await supabase
            .from('profiles')
            .update({ two_factor_enabled: false, two_factor_enrolled_at: null })
            .eq('id', userId);
        }

        // Org requires 2FA but user has no factor enrolled
        if (twoFactorRequired) {
          setMfaState({
            status: 'enroll_required',
            factors: [],
            selectedFactorId: null,
            challengeId: null
          });
          return { mfaRequired: false, mfaEnrollRequired: true };
        }
      }

      // No MFA required
      setMfaState({
        status: 'none',
        factors: [],
        selectedFactorId: null,
        challengeId: null
      });
      return { mfaRequired: false, mfaEnrollRequired: false };

    } catch (err) {
      console.error('Error checking MFA requirements:', err);
      setMfaState({
        status: REQUIRE_MFA ? 'unavailable' : 'none',
        factors: [],
        selectedFactorId: null,
        challengeId: null,
      });
      return { mfaRequired: false, mfaEnrollRequired: false };
    }
  };

  /**
   * Challenge and verify MFA with TOTP code
   */
  const verifyMfa = async (code: string): Promise<{ success: boolean; error?: string }> => {
    if (!supabase) return { success: false, error: 'Supabase not available' };

    const { selectedFactorId } = mfaState;
    if (!selectedFactorId) {
      return { success: false, error: 'No MFA factor selected' };
    }

    try {
      setMfaState(prev => ({ ...prev, status: 'verifying' }));

      // Create challenge
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: selectedFactorId
      });

      if (challengeError) {
        logSecurityEvent('login', user?.id || 'unknown', {
          method: 'mfa.challenge.start',
          error: challengeError.message
        }, 'failure');
        setMfaState(prev => ({ ...prev, status: 'required' }));
        return { success: false, error: challengeError.message };
      }

      logSecurityEvent('login', user?.id || 'unknown', {
        method: 'mfa.challenge.start',
        factorId: selectedFactorId
      }, 'success');

      // Verify with code
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: selectedFactorId,
        challengeId: challengeData.id,
        code
      });

      if (verifyError) {
        logSecurityEvent('login', user?.id || 'unknown', {
          method: 'mfa.verify.fail',
          error: verifyError.message
        }, 'failure');
        setMfaState(prev => ({ ...prev, status: 'required' }));
        return { success: false, error: 'Invalid verification code' };
      }

      const { data: assurance, error: assuranceError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (assuranceError || assurance?.currentLevel !== 'aal2') {
        setMfaState(prev => ({ ...prev, status: 'unavailable', challengeId: null }));
        return {
          success: false,
          error: 'Verification completed, but the session did not reach AAL2. Sign out and try again.',
        };
      }

      // Success - update state and log
      setMfaState(prev => ({ ...prev, status: 'verified', challengeId: challengeData.id }));

      logSecurityEvent('login', user?.id || 'unknown', {
        method: 'mfa.verify.success',
        factorId: selectedFactorId
      }, 'success');

      // Update last verified timestamp
      if (user?.id) {
        await supabase
          .from('profiles')
          .update({ two_factor_last_verified_at: new Date().toISOString() })
          .eq('id', user.id);

      }

      return { success: true };

    } catch (err) {
      console.error('MFA verification error:', err);
      setMfaState(prev => ({ ...prev, status: 'required' }));
      return { success: false, error: err instanceof Error ? err.message : 'Verification failed' };
    }
  };

  /**
   * Verify using recovery code
   */
  const verifyWithRecoveryCode = async (recoveryCode: string): Promise<{ success: boolean; error?: string }> => {
    void recoveryCode;
    // A custom database recovery code cannot mint Supabase's `aal2` JWT claim.
    // Treating one as verified would unlock the client while authoritative RLS
    // correctly remains blocked. Keep the session fail-closed until a provider-
    // backed recovery factor is implemented end to end.
    setMfaState(prev => ({ ...prev, status: 'required', challengeId: null }));
    return {
      success: false,
      error: 'Recovery-code sign-in is unavailable. Use your authenticator app or contact support.',
    };
  };

  /**
   * Reset MFA state (e.g., on logout or cancel)
   */
  const resetMfaState = () => {
    setMfaState({
      status: 'none',
      factors: [],
      selectedFactorId: null,
      challengeId: null
    });
  };

  const signOut = async () => {
    // Guard: return error if Supabase is not available
    if (!supabase) {
      return { error: new Error('Supabase not available in demo mode') };
    }

    try {
      // Log logout attempt
      if (user) {
        logSecurityEvent('logout', user.id, {
          method: 'explicit',
          email: user.email
        }, 'success');
      }

      const { error } = await supabase.auth.signOut();
      return { error };
    } catch (err) {
      console.error('Logout error:', err);
      if (user) {
        logSecurityEvent('logout', user.id, {
          method: 'explicit',
          email: user.email,
          error: err instanceof Error ? err.message : 'Unknown error'
        }, 'failure');
      }

      return { error: err instanceof Error ? err : new Error('Unknown error') };
    }
  };

  const updateProfile = async (updates: Record<string, unknown>) => {
    // Guard: return error if Supabase is not available
    if (!supabase) {
      return { data: null, error: new Error('Supabase not available in demo mode') };
    }

    try {
      const { data, error } = await supabase.auth.updateUser({
        data: updates,
      });

      // Log profile update
      if (error) {
        logSecurityEvent('user_update', user?.id || 'unknown', {
          updates,
          error: error.message
        }, 'failure');
      } else if (data.user) {
        logSecurityEvent('user_update', data.user.id, {
          updates
        }, 'success');
      }

      return { data, error };
    } catch (err) {
      console.error('Profile update error:', err);
      logSecurityEvent('user_update', user?.id || 'unknown', {
        updates,
        error: err instanceof Error ? err.message : 'Unknown error'
      }, 'failure');

      return { data: null, error: err instanceof Error ? err : new Error('Unknown error') };
    }
  };

  const resetPassword = async (email: string) => {
    // Guard: return error if Supabase is not available
    if (!supabase) {
      return { data: null, error: new Error('Supabase not available in demo mode') };
    }

    try {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });

      // Log password reset request
      if (error) {
        logSecurityEvent('password_reset_request', 'unknown', {
          email,
          error: error.message
        }, 'failure');
      } else {
        logSecurityEvent('password_reset_request', 'unknown', {
          email
        }, 'success');
      }

      return { data, error };
    } catch (err) {
      console.error('Password reset error:', err);
      logSecurityEvent('password_reset_request', 'unknown', {
        email,
        error: err instanceof Error ? err.message : 'Unknown error'
      }, 'failure');

      return { data: null, error: err instanceof Error ? err : new Error('Unknown error') };
    }
  };

  return {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    updateProfile,
    resetPassword,
    // MFA exports
    mfaState,
    verifyMfa,
    verifyWithRecoveryCode,
    resetMfaState,
  };
}
